import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { FaDownload, FaEdit, FaEye, FaFileImport, FaPlus, FaSearch, FaSyncAlt, FaTimesCircle, FaBell, FaChartLine, FaFire, FaUsers } from 'react-icons/fa';
import { getUserPrivileges, getUserRole, isAuthenticated } from '../../../utils/auth';
import api from "../../../utils/api";
import Sidebar from '../../../components/Sidebar/sidebar';
import Header from '../../../components/Header';
import './RetailProspectManage.css';
import { API_BASE_URL } from '../../../utils/apiBase';
import { PROSPECT_PRIVILEGES } from '../../../utils/prospectPrivileges';

const DEFAULT_VISIBLE_COLUMNS = {
    select: true,
    prospectName: true,
    policyName: true,
    typeOfBusiness: true,
    premiumSize: true,
    firstFollowUpDate: true,
    source: true,
    owner: true,
    actions: true
};

const formatDisplayDate = date => {
    if (!date) return '-';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const getProspectStatus = (prospect, showDisabled) => {
    if (showDisabled || prospect?.isActive === false || prospect?.disabledReason) return 'Lost';
    if (prospect?.isConvertedToPolicy) return 'Converted';
    if (prospect?.firstFollowUpDate) return 'Contacted';
    return 'New';
};

const RetailProspectManage = () => {
    const privileges = getUserPrivileges();
    const isAdmin = getUserRole() === 'admin';
    const retailPrivileges = PROSPECT_PRIVILEGES.retail;
    const hasPrivilege = (key) => isAdmin || privileges.includes(key);
    const canViewActive = hasPrivilege(retailPrivileges.viewActive);
    const canManageLostProspects = hasPrivilege(retailPrivileges.viewLost);
    const canImport = hasPrivilege(retailPrivileges.import);
    const canExport = hasPrivilege(retailPrivileges.export);
    const canEdit = hasPrivilege(retailPrivileges.edit);
    const canConvert = hasPrivilege(retailPrivileges.convert);
    const canMarkLost = hasPrivilege(retailPrivileges.markLost);
    const canReactivate = hasPrivilege(retailPrivileges.reactivate);
    const [collapsed, setCollapsed] = useState(false);
    const [prospects, setProspects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editData, setEditData] = useState(null);
    const [previousPolicies, setPreviousPolicies] = useState([]);
    const [disableId, setDisableId] = useState(null);
    const [disableReason, setDisableReason] = useState('');
    const [showDisabled, setShowDisabled] = useState(!canViewActive && canManageLostProspects);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [convertData, setConvertData] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [globalSearch, setGlobalSearch] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [statusFilter, setStatusFilter] = useState('All Statuses');
    const [sourceFilter, setSourceFilter] = useState('All Sources');
    const [assignedFilter, setAssignedFilter] = useState('All Users');
    const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
    const [showColumnMenu, setShowColumnMenu] = useState(false);
    const [selectedProspectIds, setSelectedProspectIds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const navigate = useNavigate();
    const [editErrors, setEditErrors] = useState({
        previousPolicyNumber: ''
    });
    const isEditFormInvalid = Boolean(editErrors.previousPolicyNumber);


    const [premiumDisplay, setPremiumDisplay] = useState('');
    const [premiumError, setPremiumError] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const safeSortConfig = sortConfig || { key: null, direction: 'asc' };

    const [bulkLostModal, setBulkLostModal] = useState({ open: false, reason: '' });

    const [inlineEdit, setInlineEdit] = useState(null); // { id, field, value, saving, error }
    const tableWrapRef = useRef(null);


    const formatDateForInput = date =>
        date ? new Date(date).toISOString().split('T')[0] : '';

    // ================= FETCH DATA =================
    const loadProspects = useCallback(async () => {
        const url = showDisabled
            ? `${API_BASE_URL}/api/manage-prospects/retail/disabled`
            : `${API_BASE_URL}/api/manage-prospects/retail`;

        setLoading(true);
        try {
            const res = await api.get(url);
            setProspects(res.data);
        } catch (err) {
            if (err.response?.status === 401) {
                window.location.href = "/login";
            } else if (err.response?.status === 403) {
                navigate("/unauthorized");
            } else {
                console.error("Failed to load prospects", err);
            }
        } finally {
            setLoading(false);
        }
    }, [navigate, showDisabled]);

    useEffect(() => {
        loadProspects();
        setSelectedProspectIds([]);
        setCurrentPage(1);
    }, [loadProspects, showDisabled]);

    useEffect(() => {
        if (
            !editData ||
            editData.typeOfBusiness !== 'RENEWAL' ||
            !editData.prospectName
        ) {
            setPreviousPolicies([]);
            return;
        }

        const fetchPreviousPolicies = async () => {
            try {
                const res = await api.get(
                    `${API_BASE_URL}/api/prospects/previous-policies`,
                    {
                        params: { clientName: editData.prospectName }
                    }
                );
                setPreviousPolicies(res.data);
            } catch (err) {
                console.error('Failed to fetch previous policies', err);
                setPreviousPolicies([]);
            }
        };

        fetchPreviousPolicies();
    }, [editData]);

    const formatINR = value =>
        value ? new Intl.NumberFormat('en-IN').format(value) : '';

    const parseINR = value => value.replace(/[^0-9]/g, '');

    useEffect(() => {
        if (!editData?.premiumSize) return;
        setPremiumDisplay(formatINR(editData.premiumSize));
    }, [editData]);


    const validateEditPreviousPolicyNumber = (value, typeOfBusiness) => {
        if (typeOfBusiness !== 'ROLLOVER') {
            return '';
        }

        if (!value) return ''; // optional

        const allowedCharsRegex = /^[A-Z0-9/-]+$/i;

        if (!allowedCharsRegex.test(value)) {
            return 'Only letters, numbers, / and - are allowed';
        }

        if (value.length < 8) {
            return 'Policy number is too short';
        }

        if (value.length > 30) {
            return 'Policy number seems too long';
        }

        return '';
    };

    useEffect(() => {
        setEditErrors(prev => ({
            ...prev,
            previousPolicyNumber: ''
        }));
    }, [editData]);


    // ================= UPDATE =================
    const handleUpdate = async () => {
        const payload = {
            premiumSize: editData.premiumSize,
            firstFollowUpDate: editData.firstFollowUpDate,
            remarks: editData.remarks
        };

        await api.put(
            `${API_BASE_URL}/api/manage-prospects/${editData._id}`,
            payload
        );

        setEditData(null);
        loadProspects();
    };

    const getSortableValue = (prospect, key) => {
        if (key === 'premiumSize') {
            const value = Number(prospect.premiumSize);
            return Number.isNaN(value) ? 0 : value;
        }

        if (key === 'firstFollowUpDate') {
            const value = prospect.firstFollowUpDate ? new Date(prospect.firstFollowUpDate).getTime() : 0;
            return Number.isNaN(value) ? 0 : value;
        }

        return String(prospect[key] || '').toLowerCase();
    };

    const sourceOptions = useMemo(() => {
        const values = prospects
            .map(p => p.source || p.sourceName || p.leadSource || '')
            .filter(Boolean);
        return ['All Sources', ...new Set(values)];
    }, [prospects]);

    const assignedOptions = useMemo(() => {
        const values = prospects
            .map(p => p.ownerId?.name || p.ownerName || p.assignedUser || '')
            .filter(Boolean);
        return ['All Users', ...new Set(values)];
    }, [prospects]);

    const filteredProspects = prospects.filter((prospect) => {
        const search = globalSearch.trim().toLowerCase();
        const searchableText = [
            prospect.prospectName,
            prospect.policyName,
            prospect.typeOfBusiness,
            prospect.mobileNumber,
            prospect.phoneNumber,
            prospect.email,
            prospect.emailId,
            prospect.remarks,
            prospect.source,
            prospect.ownerId?.name
        ].join(' ').toLowerCase();

        const followUpTime = prospect.firstFollowUpDate
            ? new Date(prospect.firstFollowUpDate).getTime()
            : null;
        const fromTime = dateFrom ? new Date(dateFrom).getTime() : null;
        const toTime = dateTo ? new Date(dateTo).getTime() : null;
        const prospectSource = prospect.source || prospect.sourceName || prospect.leadSource || '';
        const assignedUser = prospect.ownerId?.name || prospect.ownerName || prospect.assignedUser || '';
        const computedStatus = getProspectStatus(prospect, showDisabled);

        return (
            (!search || searchableText.includes(search)) &&
            (!fromTime || (followUpTime && followUpTime >= fromTime)) &&
            (!toTime || (followUpTime && followUpTime <= toTime)) &&
            (statusFilter === 'All Statuses' || computedStatus === statusFilter) &&
            (sourceFilter === 'All Sources' || prospectSource === sourceFilter) &&
            (assignedFilter === 'All Users' || assignedUser === assignedFilter)
        );
    });

    const sortedProspects = [...filteredProspects].sort((a, b) => {
        if (!safeSortConfig.key) return 0;

        const valueA = getSortableValue(a, safeSortConfig.key);
        const valueB = getSortableValue(b, safeSortConfig.key);

        if (valueA < valueB) return safeSortConfig.direction === 'asc' ? -1 : 1;
        if (valueA > valueB) return safeSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const totalPages = Math.max(1, Math.ceil(sortedProspects.length / pageSize));
    const paginatedProspects = sortedProspects.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [globalSearch, dateFrom, dateTo, statusFilter, sourceFilter, assignedFilter]);

    useEffect(() => {
        const el = tableWrapRef.current;
        if (!el) return;

        const onWheel = (ev) => {
            // If the table area can scroll, keep the scroll inside it (trackpad-friendly).
            const canScrollY = el.scrollHeight > el.clientHeight;
            const canScrollX = el.scrollWidth > el.clientWidth;
            if (!canScrollY && !canScrollX) return;

            const deltaY = ev.deltaY || 0;
            const deltaX = ev.deltaX || 0;

            // Prefer vertical scrolling; shift+wheel or horizontal delta should scroll X.
            if (Math.abs(deltaX) > Math.abs(deltaY) || ev.shiftKey) {
                if (!canScrollX) return;
                el.scrollLeft += ev.shiftKey ? deltaY : deltaX;
                ev.preventDefault();
                return;
            }

            if (!canScrollY) return;

            const prevTop = el.scrollTop;
            el.scrollTop += deltaY;

            // If we actually consumed scroll, prevent page from scrolling.
            if (el.scrollTop !== prevTop) {
                ev.preventDefault();
            }
        };

        // passive:false is required to allow preventDefault()
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, []);

    const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length + (showDisabled ? 1 : 0);

    const renderColGroup = () => (
        <colgroup>
            {visibleColumns.select && <col style={{ width: 62 }} />}
            {visibleColumns.prospectName && <col style={{ width: 300 }} />}
            {visibleColumns.policyName && <col style={{ width: 480 }} />}
            {visibleColumns.typeOfBusiness && <col style={{ width: 140 }} />}
            {visibleColumns.premiumSize && <col style={{ width: 170 }} />}
            {visibleColumns.firstFollowUpDate && <col style={{ width: 170 }} />}
            {visibleColumns.source && <col style={{ width: 150 }} />}
            {visibleColumns.owner && <col style={{ width: 150 }} />}
            {showDisabled && <col style={{ width: 220 }} />}
            {visibleColumns.actions && <col style={{ width: 210 }} />}
        </colgroup>
    );

    const renderTableHeader = () => (
        <tr>
            {visibleColumns.select && (
                <th className="select-col">
                    <input
                        type="checkbox"
                        checked={paginatedProspects.length > 0 && paginatedProspects.every(p => selectedProspectIds.includes(p._id))}
                        onChange={toggleAllVisibleProspects}
                    />
                </th>
            )}
            {visibleColumns.prospectName && (
                <th
                    data-col="name"
                    onClick={() => handleSort('prospectName')}
                >
                    <div className="th-inner">
                        <span>Name {getSortIndicator('prospectName')}</span>
                    </div>
                </th>
            )}
            {visibleColumns.policyName && (
                <th onClick={() => handleSort('policyName')}>
                    <div className="th-inner">
                        <span>Policy {getSortIndicator('policyName')}</span>
                    </div>
                </th>
            )}
            {visibleColumns.typeOfBusiness && (
                <th onClick={() => handleSort('typeOfBusiness')}>
                    <div className="th-inner">
                        <span>Type {getSortIndicator('typeOfBusiness')}</span>
                    </div>
                </th>
            )}
            {visibleColumns.premiumSize && (
                <th onClick={() => handleSort('premiumSize')}>
                    <div className="th-inner">
                        <span>Premium {getSortIndicator('premiumSize')}</span>
                    </div>
                </th>
            )}
            {visibleColumns.firstFollowUpDate && (
                <th onClick={() => handleSort('firstFollowUpDate')}>
                    <div className="th-inner">
                        <span>Follow-up {getSortIndicator('firstFollowUpDate')}</span>
                    </div>
                </th>
            )}
            {visibleColumns.source && (
                <th>
                    <div className="th-inner">
                        <span>Source</span>
                    </div>
                </th>
            )}
            {visibleColumns.owner && (
                <th>
                    <div className="th-inner">
                        <span>Assigned</span>
                    </div>
                </th>
            )}
            {showDisabled && <th>Lost Reason</th>}
            {visibleColumns.actions && <th className="actions-col">Actions</th>}
        </tr>
    );

    const toggleProspectSelection = (id) => {
        setSelectedProspectIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const toggleAllVisibleProspects = () => {
        const pageIds = paginatedProspects.map(p => p._id);
        const allSelected = pageIds.length > 0 && pageIds.every(id => selectedProspectIds.includes(id));

        setSelectedProspectIds(prev =>
            allSelected
                ? prev.filter(id => !pageIds.includes(id))
                : [...new Set([...prev, ...pageIds])]
        );
    };

    const exportProspects = (rows = sortedProspects) => {
        const selectedRows = selectedProspectIds.length
            ? rows.filter(row => selectedProspectIds.includes(row._id))
            : rows;

        if (!selectedRows.length) {
            alert('No prospects available to export');
            return;
        }

        const csvRows = [
            ['Name', 'Policy', 'Type', 'Premium', 'Follow-up', 'Status'],
            ...selectedRows.map(p => [
                p.prospectName || '',
                p.policyName || '',
                p.typeOfBusiness || '',
                p.premiumSize || '',
                formatDisplayDate(p.firstFollowUpDate),
                getProspectStatus(p, showDisabled)
            ])
        ];

        const csv = csvRows
            .map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'retail-prospects.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleSort = (columnKey) => {
        setSortConfig((prev) => {
            const current = prev || { key: null, direction: 'asc' };
            if (current.key === columnKey) {
                return { key: columnKey, direction: current.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key: columnKey, direction: 'asc' };
        });
    };

    const getSortIndicator = (columnKey) => {
        if (safeSortConfig.key !== columnKey) return '';
        return safeSortConfig.direction === 'asc' ? '▲' : '▼';
    };

    const openInlineEdit = (prospect, field) => {
        if (!canEdit || showDisabled) return;
        const currentValue = field === 'firstFollowUpDate'
            ? formatDateForInput(prospect.firstFollowUpDate)
            : (prospect[field] || '');
        setInlineEdit({ id: prospect._id, field, value: currentValue, saving: false, error: '' });
    };

    const cancelInlineEdit = () => setInlineEdit(null);

    const saveInlineEdit = async () => {
        if (!inlineEdit?.id || !inlineEdit?.field) return;
        const { id, field } = inlineEdit;

        setInlineEdit((prev) => ({ ...prev, saving: true, error: '' }));

        const payload = field === 'firstFollowUpDate'
            ? { firstFollowUpDate: inlineEdit.value || null }
            : { [field]: inlineEdit.value };

        try {
            await api.put(`${API_BASE_URL}/api/manage-prospects/${id}`, payload);
            setInlineEdit(null);
            loadProspects();
        } catch (err) {
            const message = err?.response?.data?.error || 'Failed to save changes';
            setInlineEdit((prev) => ({ ...prev, saving: false, error: message }));
        }
    };

    const resetProspectFilters = () => {
        setGlobalSearch('');
        setDateFrom('');
        setDateTo('');
        setStatusFilter('All Statuses');
        setSourceFilter('All Sources');
        setAssignedFilter('All Users');
    };


    if (!isAuthenticated()) {
        return <Navigate to="/" />;
    }

    return (
        <div className="retail-policy-manage-prospect-container">
            <div className="sidebar">
                <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            </div>
            <div className={`retail-policy-manage-prospect-content ${collapsed ? 'collapsed' : ''}`}>
                <div className="retail-policy-manage-prospect-sticky-header">
                    <Header />
                </div>
                <div className="retail-policy-manage-prospect-scroll-area">
                    <section className="retail-prospect-shell policy-table-section">
                        <div className="retail-prospect-hero converted-policy-page-hero">
                            <div>
                                <span className="retail-prospect-eyebrow converted-policy-eyebrow">Transactions / Manage Prospects / Retail</span>
                                <h1>Retail Prospects</h1>
                                <p>{sortedProspects.length} of {prospects.length} records visible</p>
                            </div>

                            <div className="retail-prospect-quick-actions">
                                <button className="converted-policy-reset-btn" type="button" onClick={resetProspectFilters}>
                                    Reset filters
                                </button>
                                <button type="button" onClick={() => navigate('/prospect/retail-prospect')} title="Add Prospect">
                                    <FaPlus /> Add Prospect
                                </button>
                                {canImport && <button type="button" onClick={() => alert('Import flow is not configured yet')} title="Import Prospects">
                                    <FaFileImport /> Import
                                </button>}
                                {canExport && <button type="button" onClick={() => exportProspects()} title="Export current prospects">
                                    <FaDownload /> Export
                                </button>}
                            </div>
                        </div>

                        <div className="prospect-summary-grid converted-policy-summary-grid">
                            <div className="prospect-summary-card converted-policy-summary-card blue">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FaUsers style={{ color: '#1d4ed8', fontSize: '1.3rem' }} />
                                    <span>Total</span>
                                </div>
                                <strong>{prospects.length}</strong>
                                <small>{showDisabled ? 'Lost prospects' : 'Active prospects'}</small>
                            </div>
                            <div className="prospect-summary-card converted-policy-summary-card amber hot">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FaFire style={{ color: '#b45309', fontSize: '1.3rem' }} />
                                    <span>Hot</span>
                                </div>
                                <strong>{prospects.filter(p => Number(p.premiumSize || 0) >= 100000).length}</strong>
                                <small>Premium ≥ ₹1,00,000</small>
                            </div>
                            <div className="prospect-summary-card converted-policy-summary-card green">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FaChartLine style={{ color: '#059669', fontSize: '1.3rem' }} />
                                    <span>Visible</span>
                                </div>
                                <strong>{filteredProspects.length}</strong>
                                <small>After filters</small>
                            </div>
                            <div className="prospect-summary-card converted-policy-summary-card violet">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FaBell style={{ color: '#7c3aed', fontSize: '1.3rem' }} />
                                    <span>Selected</span>
                                </div>
                                <strong>{selectedProspectIds.length}</strong>
                                <small>Bulk actions ready</small>
                            </div>
                        </div>

                        <div className="retail-prospect-controls-card converted-policy-filter-panel">
                            <label className="prospect-global-search converted-policy-search">
                                <FaSearch />
                                <input
                                    value={globalSearch}
                                    onChange={(e) => setGlobalSearch(e.target.value)}
                                    placeholder="Search name, mobile, email, policy, remarks..."
                                />
                            </label>

                            <div className="prospect-filter-grid converted-policy-table-controls">
                                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                    {['All Statuses', 'New', 'Contacted', 'Converted', 'Lost'].map(option => (
                                        <option key={option}>{option}</option>
                                    ))}
                                </select>
                                <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                                    {sourceOptions.map(option => <option key={option}>{option}</option>)}
                                </select>
                                <select value={assignedFilter} onChange={(e) => setAssignedFilter(e.target.value)}>
                                    {assignedOptions.map(option => <option key={option}>{option}</option>)}
                                </select>
                            </div>

                            <div className="retail-prospect-toolbar">
                                {canViewActive && <button className={`prospect-filter-btn ${!showDisabled ? 'active' : ''}`} onClick={() => setShowDisabled(false)}>
                                    Active Prospects
                                </button>}

                                {canManageLostProspects && (
                                    <button className={`prospect-filter-btn prospect-filter-btn-warning ${showDisabled ? 'active' : ''}`} onClick={() => setShowDisabled(true)}>
                                        Lost Prospects
                                    </button>
                                )}

                                <button type="button" className="prospect-filter-btn" onClick={loadProspects}>
                                    <FaSyncAlt /> Refresh
                                </button>

                                <div className="column-toggle-wrap">
                                    <button type="button" className="prospect-filter-btn" onClick={() => setShowColumnMenu(prev => !prev)}>
                                        Columns
                                    </button>
                                    {showColumnMenu && (
                                        <div className="column-toggle-menu">
                                            {Object.keys(DEFAULT_VISIBLE_COLUMNS).map(columnKey => (
                                                <label key={columnKey}>
                                                    <input
                                                        type="checkbox"
                                                        checked={visibleColumns[columnKey]}
                                                        onChange={() => setVisibleColumns(prev => ({
                                                            ...prev,
                                                            [columnKey]: !prev[columnKey]
                                                        }))}
                                                    />
                                                    {columnKey === 'select' ? 'Select' : columnKey.replace(/([A-Z])/g, ' $1')}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {selectedProspectIds.length > 0 && (
                                    <>
                                        {canExport && <button type="button" className="prospect-filter-btn prospect-filter-btn-primary" onClick={() => exportProspects()}>
                                            Export Selected ({selectedProspectIds.length})
                                        </button>}
                                        {canMarkLost && !showDisabled && (
                                            <button
                                                type="button"
                                                className="prospect-filter-btn prospect-filter-btn-warning"
                                                onClick={() => setBulkLostModal({ open: true, reason: '' })}
                                            >
                                                Mark Lost (Bulk)
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="prospect-active-filter-chips converted-policy-filter-chips" aria-label="Active prospect filters">
                                <span>{showDisabled ? 'Lost pipeline' : 'Active pipeline'}</span>
                                {statusFilter !== 'All Statuses' && <span>Status: {statusFilter}</span>}
                                {sourceFilter !== 'All Sources' && <span>Source: {sourceFilter}</span>}
                                {assignedFilter !== 'All Users' && <span>Assigned: {assignedFilter}</span>}
                                {(dateFrom || dateTo) && <span>Follow-up window</span>}
                                {globalSearch.trim() && <span>Search active</span>}
                            </div>
                        </div>

                        {selectedProspectIds.length > 0 && (
                            <div className="converted-policy-bulkbar">
                                <strong>{selectedProspectIds.length} selected</strong>
                                <span>Use row actions to preview, edit, convert, mark lost, export, or reactivate prospects.</span>
                            </div>
                        )}

                        <div className="prospect-table-card retail-prospect-table-scope retail-prospects-table-component">
                            <div className="prospect-table-card-head">
                                <div>
                                    <span>Retail prospect list</span>
                                    <strong>{sortedProspects.length} records</strong>
                                </div>
                            </div>

                            <div className="prospect-table-wrap retail-prospects-table-scroll converted-policy-table-scroll" ref={tableWrapRef}>
                                <table className="prospect-table retail-prospects-table converted-policy-table">
                                    {renderColGroup()}
                                    <thead>
                                        {renderTableHeader()}
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            Array.from({ length: 5 }).map((_, index) => (
                                                <tr key={index} className="prospect-skeleton-row">
                                                    <td colSpan={visibleColumnCount}>
                                                        <span />
                                                    </td>
                                                </tr>
                                            ))
                                        ) : paginatedProspects.length ? (
                                            paginatedProspects.map(p => {
                                                const status = getProspectStatus(p, showDisabled);
                                                const isHot = Number(p.premiumSize || 0) >= 100000;

                                                return (
                                                    <tr key={p._id} className={isHot ? 'hot-prospect-row' : ''}>
                                                        {visibleColumns.select && (
                                                            <td className="select-col">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedProspectIds.includes(p._id)}
                                                                    onChange={() => toggleProspectSelection(p._id)}
                                                                />
                                                            </td>
                                                        )}
                                                        {visibleColumns.prospectName && (
                                                            <td data-label="Name">
                                                                <div className="prospect-person-cell">
                                                                    <span className="prospect-avatar">{(p.prospectName || '?').charAt(0).toUpperCase()}</span>
                                                                    <div>
                                                                        <strong>{p.prospectName || '-'}</strong>
                                                                        <span className={`prospect-status-badge status-${status.toLowerCase()}`}>{status}</span>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        )}
                                                        {visibleColumns.policyName && <td data-label="Policy">{p.policyName || '-'}</td>}
                                                        {visibleColumns.typeOfBusiness && <td data-label="Type">{p.typeOfBusiness || '-'}</td>}
                                                        {visibleColumns.premiumSize && (
                                                            <td data-label="Premium">
                                                                <strong className={isHot ? 'hot-premium' : ''}>₹{formatINR(p.premiumSize)}</strong>
                                                            </td>
                                                        )}
                                                        {visibleColumns.firstFollowUpDate && (
                                                            <td
                                                                data-label="Follow-up"
                                                                className={canEdit && !showDisabled ? 'editable-cell' : undefined}
                                                                onDoubleClick={() => openInlineEdit(p, 'firstFollowUpDate')}
                                                            >
                                                                {inlineEdit?.id === p._id && inlineEdit.field === 'firstFollowUpDate' ? (
                                                                    <div className="inline-edit-wrap" onClick={(e) => e.stopPropagation()}>
                                                                        <input
                                                                            type="date"
                                                                            value={inlineEdit.value}
                                                                            onChange={(e) => setInlineEdit((prev) => ({ ...prev, value: e.target.value }))}
                                                                            disabled={inlineEdit.saving}
                                                                        />
                                                                        <div className="inline-edit-actions">
                                                                            <button type="button" onClick={saveInlineEdit} disabled={inlineEdit.saving}>Save</button>
                                                                            <button type="button" onClick={cancelInlineEdit} disabled={inlineEdit.saving}>Cancel</button>
                                                                        </div>
                                                                        {inlineEdit.error && <small className="field-error">{inlineEdit.error}</small>}
                                                                    </div>
                                                                ) : (
                                                                    formatDisplayDate(p.firstFollowUpDate)
                                                                )}
                                                            </td>
                                                        )}
                                                        {visibleColumns.source && <td data-label="Source">{p.source || p.sourceName || p.leadSource || '-'}</td>}
                                                        {visibleColumns.owner && <td data-label="Assigned">{p.ownerId?.name || p.ownerName || '-'}</td>}
                                                        {showDisabled && (
                                                            <td data-label="Lost Reason" className="prospect-lost-reason">
                                                                {p.disabledReason || '-'}
                                                            </td>
                                                        )}
                                                        {visibleColumns.actions && (
                                                            <td data-label="Actions" className="actions-col">
                                                                <div className="prospect-action-group converted-policy-actions">
                                                                    <button className="icon-action view" title="Preview" data-tooltip="Preview" onClick={() => setPreviewData(p)}>
                                                                        <FaEye />
                                                                    </button>
                                                                    {!showDisabled ? (
                                                                        <>
                                                                            {canEdit && (
                                                                                <button className="icon-action edit" title="Edit" data-tooltip="Edit" onClick={() => setEditData(p)}>
                                                                                    <FaEdit />
                                                                                </button>
                                                                            )}

                                                                            {canConvert && (
                                                                                <button className="icon-action convert" title="Convert" data-tooltip="Convert" onClick={() => setConvertData(p)}>
                                                                                    <FaSyncAlt />
                                                                                </button>
                                                                            )}

                                                                            {canMarkLost && (
                                                                                <button
                                                                                    className="icon-action danger"
                                                                                    title="Mark as Lost"
                                                                                    data-tooltip="Mark Lost"
                                                                                    onClick={() => {
                                                                                        setDisableId(p._id);
                                                                                        setDisableReason('');
                                                                                    }}
                                                                                >
                                                                                    <FaTimesCircle />
                                                                                </button>
                                                                            )}

                                                                        </>
                                                                    ) : canReactivate ? (
                                                                        <button
                                                                            className="icon-action convert"
                                                                            title="Activate"
                                                                            data-tooltip="Activate"
                                                                            onClick={async () => {
                                                                                await api.put(`${API_BASE_URL}/api/manage-prospects/activate/${p._id}`);
                                                                                loadProspects();
                                                                            }}
                                                                        >
                                                                            <FaSyncAlt />
                                                                        </button>
                                                                    ) : null}
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={visibleColumnCount} className="prospect-empty-state">
                                                    <strong>No retail prospects found</strong>
                                                    <span>Try changing filters or add a new prospect to begin.</span>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="prospect-pagination converted-policy-pagination-row">
                                <span>
                                    Page {currentPage} of {totalPages} · {sortedProspects.length} records
                                </span>
                                <div>
                                    <button type="button" disabled={currentPage === 1} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}>
                                        Previous
                                    </button>
                                    <button type="button" disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}>
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>

                        {previewData && (
                            <div className="prospect-preview-overlay" onClick={() => setPreviewData(null)}>
                                <aside className="prospect-preview-drawer" onClick={(e) => e.stopPropagation()}>
                                    <div className="preview-drawer-head">
                                        <div>
                                            <span>Quick Preview</span>
                                            <h3>{previewData.prospectName || '-'}</h3>
                                        </div>
                                        <button type="button" onClick={() => setPreviewData(null)}>×</button>
                                    </div>

                                    <div className="preview-detail-grid">
                                        <p><strong>Policy:</strong> {previewData.policyName || '-'}</p>
                                        <p><strong>Class:</strong> {previewData.classOfPolicyName || '-'}</p>
                                        <p><strong>Type:</strong> {previewData.typeOfBusiness || '-'}</p>
                                        <p><strong>Premium:</strong> ₹{formatINR(previewData.premiumSize)}</p>
                                        <p><strong>Follow-up:</strong> {formatDisplayDate(previewData.firstFollowUpDate)}</p>
                                        <p><strong>Renewal Date:</strong> {formatDisplayDate(previewData.tentativeStartDate)}</p>
                                        <p><strong>Previous Policy:</strong> {previewData.previousPolicyNumber || '-'}</p>
                                        <p><strong>Status:</strong> {getProspectStatus(previewData, showDisabled)}</p>
                                        <p className="full"><strong>Remarks:</strong> {previewData.remarks || '-'}</p>
                                        {showDisabled && (
                                            <p className="full"><strong>Lost Reason:</strong> {previewData.disabledReason || '-'}</p>
                                        )}
                                    </div>

                                    <div className="preview-actions">
                                        {canEdit && !showDisabled && (
                                            <button type="button" className="btn-primary-prospect-manage" onClick={() => {
                                                setEditData(previewData);
                                                setPreviewData(null);
                                            }}>
                                                Edit Prospect
                                            </button>
                                        )}
                                        {canConvert && !showDisabled && (
                                            <button type="button" className="btn-secondary-prospect-manage" onClick={() => {
                                                setConvertData(previewData);
                                                setPreviewData(null);
                                            }}>
                                                Convert
                                            </button>
                                        )}
                                    </div>
                                </aside>
                            </div>
                        )}

                        {convertData && (
                            <div className="edit-modal-overlay prospect-action-modal-overlay" onClick={() => setConvertData(null)}>
                                <div className="prospect-action-modal prospect-action-modal-convert" onClick={(e) => e.stopPropagation()}>
                                    <div className="prospect-action-modal-head">
                                        <span className="prospect-action-modal-icon convert">
                                            <FaSyncAlt />
                                        </span>
                                        <div>
                                            <span className="prospect-action-modal-kicker">Convert Prospect</span>
                                            <h3>Convert to policy?</h3>
                                            <p>Create a new policy draft using this prospect's existing details.</p>
                                        </div>
                                    </div>

                                    <div className="prospect-action-modal-summary">
                                        <div>
                                            <span>Prospect</span>
                                            <strong>{convertData.prospectName || '-'}</strong>
                                        </div>
                                        <div>
                                            <span>Policy</span>
                                            <strong>{convertData.policyName || '-'}</strong>
                                        </div>
                                        <div>
                                            <span>Premium</span>
                                            <strong>₹{formatINR(convertData.premiumSize) || '-'}</strong>
                                        </div>
                                    </div>

                                    <div className="prospect-action-modal-note">
                                        You can review and complete all remaining policy fields before final submission.
                                    </div>

                                    <div className="prospect-action-modal-actions">
                                        <button
                                            className="btn-primary-prospect-manage prospect-action-primary"
                                            onClick={() => {
                                                navigate('/add-policy', {
                                                    state: {
                                                        fromProspect: true,
                                                        prospectData: {
                                                            ...convertData,
                                                            clientType: 'Retail'
                                                        }
                                                    }
                                                });
                                            }}
                                        >
                                            <FaSyncAlt />
                                            Yes, Convert
                                        </button>

                                        <button
                                            className="btn-secondary-prospect-manage prospect-action-secondary"
                                            onClick={() => setConvertData(null)}
                                        >
                                            Cancel
                                        </button>
                                    </div>

                                </div>
                            </div>
                        )}


                        {disableId && (
                            <div className="edit-modal-overlay prospect-action-modal-overlay" onClick={() => setDisableId(null)}>
                                <div className="prospect-action-modal prospect-action-modal-lost" onClick={(e) => e.stopPropagation()}>
                                    <div className="prospect-action-modal-head">
                                        <span className="prospect-action-modal-icon lost">
                                            <FaTimesCircle />
                                        </span>
                                        <div>
                                            <span className="prospect-action-modal-kicker">Mark Lost</span>
                                            <h3>Move prospect to lost?</h3>
                                            <p>This keeps the record available in Lost Prospects with the reason captured.</p>
                                        </div>
                                    </div>

                                    <div className="prospect-action-modal-field">
                                        <label>Reason for lost <span>*</span></label>
                                        <textarea
                                            value={disableReason}
                                            onChange={e => setDisableReason(e.target.value)}
                                            placeholder="Example: Client chose another insurer, pricing mismatch, or no response..."
                                            required
                                        />
                                        <small>{disableReason.trim().length}/250 characters</small>
                                    </div>

                                    <div className="prospect-action-modal-actions">
                                        <button
                                            className="btn-danger-prospect-manage prospect-action-danger"
                                            onClick={async () => {
                                                if (!disableReason.trim()) {
                                                    alert('Reason is required');
                                                    return;
                                                }

                                                await api.put(
                                                    `${API_BASE_URL}/api/manage-prospects/disable/${disableId}`,
                                                    { reason: disableReason }
                                                );

                                                setDisableId(null);
                                                setDisableReason('');
                                                loadProspects();
                                            }}
                                        >
                                            <FaTimesCircle />
                                            Confirm Lost
                                        </button>

                                        <button
                                            className="btn-secondary-prospect-manage prospect-action-secondary"
                                            onClick={() => setDisableId(null)}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}


                        {/* ================= EDIT MODAL ================= */}
                        {editData && (
                            <div className="edit-modal-overlay">
                                <div className="edit-modal">
                                    <h3>Edit Prospect</h3>

                                    {/* Prospect Name */}
                                    <div className="edit-modal-body">
                                        <div className="modal-field">
                                            <label>Prospect Name</label>
                                            <input
                                                value={editData.prospectName}
                                                className="read-only-input"
                                                title="To change prospect, please select a different one"
                                            />
                                        </div>

                                        {/* <div className="modal-field">
                                        <label>Category</label>
                                        <input
                                            type="text"
                                            value={
                                                categories.find(c => c._id === selectedCategoryId)?.categoryName || ''
                                            }
                                            readOnly
                                            className="read-only-input"
                                        />
                                    </div> */}
                                        <div className="modal-field">
                                            <label>Class of Policy</label>

                                            <input
                                                type="text"
                                                value={editData.classOfPolicyName}
                                                readOnly
                                                className="read-only-input"
                                            />

                                        </div>

                                        {/* Policy Name */}
                                        <div className="modal-field">
                                            <label>Policy Name</label>
                                            <input
                                                type="text"
                                                value={editData.policyName}
                                                readOnly
                                                className="read-only-input"
                                            />
                                        </div>


                                        {/* Type of Business */}
                                        <div className="modal-field">
                                            <label>Type of Business</label>
                                            <input
                                                type="text"
                                                value={
                                                    editData.typeOfBusiness === 'NEW'
                                                        ? 'New'
                                                        : editData.typeOfBusiness === 'RENEWAL'
                                                            ? 'I-Pro Renewal'
                                                            : 'Other Renewal'
                                                }
                                                readOnly
                                                className="read-only-input"
                                            />
                                        </div>

                                        {/* Previous Policy Number (ONLY IF RENEWAL) */}
                                        <div className="modal-field">
                                            <label>Previous Policy Number</label>

                                            {editData.typeOfBusiness === 'RENEWAL' ? (
                                                /* ===== DROPDOWN FOR RENEWAL ===== */
                                                <select
                                                    value={editData.previousPolicyNumber || ''}
                                                    onChange={e => {
                                                        const value = e.target.value.toUpperCase();
                                                        const errorMsg = validateEditPreviousPolicyNumber(
                                                            value,
                                                            editData.typeOfBusiness
                                                        );

                                                        setEditData({
                                                            ...editData,
                                                            previousPolicyNumber: value
                                                        });

                                                        setEditErrors(prev => ({
                                                            ...prev,
                                                            previousPolicyNumber: errorMsg
                                                        }));
                                                    }}
                                                    className={editErrors.previousPolicyNumber ? 'input-error' : ''}
                                                >
                                                    <option value="">Select Previous Policy</option>

                                                    {previousPolicies.map(p => (
                                                        <option key={p._id} value={p.policyNumber}>
                                                            {p.policyNumber}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                /* ===== INPUT FOR NEW & ROLLOVER ===== */
                                                <input
                                                    type="text"
                                                    placeholder="Previous policy number (if any)"
                                                    value={editData.previousPolicyNumber || ''}
                                                    onChange={e => {
                                                        const value = e.target.value.toUpperCase();
                                                        const errorMsg = validateEditPreviousPolicyNumber(
                                                            value,
                                                            editData.typeOfBusiness
                                                        );

                                                        setEditData({
                                                            ...editData,
                                                            previousPolicyNumber: value
                                                        });

                                                        setEditErrors(prev => ({
                                                            ...prev,
                                                            previousPolicyNumber: errorMsg
                                                        }));
                                                    }}
                                                    className={editErrors.previousPolicyNumber ? 'input-error' : ''}
                                                />
                                            )}

                                            {/* 🔴 INLINE ERROR */}
                                            {editErrors.previousPolicyNumber && (
                                                <span className="field-error">
                                                    {editErrors.previousPolicyNumber}
                                                </span>
                                            )}
                                        </div>




                                        {/* Gross Premium */}
                                        {/* <div className="modal-field">
                                        <label>Gross Premium</label>
                                        <input
                                            type="number"
                                            value={editData.grossPremium}
                                            onChange={e =>
                                                setEditData({ ...editData, grossPremium: e.target.value })
                                            }
                                        />
                                    </div> */}

                                        {/* Premium Size */}
                                        <div className="modal-field">
                                            <label>Premium Amount</label>

                                            <div style={{ position: 'relative' }}>
                                                <span style={{ position: 'absolute', left: 10, top: '50%' }}>₹</span>

                                                <input
                                                    type="text"
                                                    value={premiumDisplay}
                                                    onChange={e => {
                                                        const raw = parseINR(e.target.value);

                                                        if (!raw) {
                                                            setPremiumDisplay('');
                                                            setPremiumError('');
                                                            setEditData(prev => ({ ...prev, premiumSize: '' }));
                                                            return;
                                                        }

                                                        const num = Number(raw);
                                                        if (num > 100000000) {
                                                            setPremiumError('Maximum allowed premium is ₹10,00,00,000');
                                                            return;
                                                        }

                                                        setPremiumError('');
                                                        setPremiumDisplay(formatINR(num));
                                                        setEditData(prev => ({ ...prev, premiumSize: num }));
                                                    }}
                                                />
                                            </div>

                                            {premiumError && <small className="field-error">{premiumError}</small>}
                                        </div>


                                        {/* Tentative Start Date */}
                                        <div className="modal-field">
                                            <label>Renewal Date</label>
                                            <input
                                                type="date"
                                                value={formatDateForInput(editData.tentativeStartDate)}
                                                onChange={e =>
                                                    setEditData({
                                                        ...editData,
                                                        tentativeStartDate: e.target.value
                                                    })
                                                }
                                            />
                                        </div>

                                        {/* First Follow-up Date */}
                                        <div className="modal-field">
                                            <label>First Follow-up Date</label>
                                            <input
                                                type="date"
                                                value={formatDateForInput(editData.firstFollowUpDate)}
                                                onChange={e =>
                                                    setEditData({
                                                        ...editData,
                                                        firstFollowUpDate: e.target.value
                                                    })
                                                }
                                            />
                                        </div>

                                        {/* Remarks */}
                                        <div className="modal-field">
                                            <label>Remarks</label>
                                            <textarea
                                                value={editData.remarks || ''}
                                                onChange={e =>
                                                    setEditData({ ...editData, remarks: e.target.value })
                                                }
                                            />
                                        </div>

                                        {editData.rolloverPolicyDocPath && (
                                            <div className="modal-field">
                                                <label>Previous Policy Document</label>
                                                <a
                                                    href={`${API_BASE_URL}/${editData.rolloverPolicyDocPath}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="doc-view-link"
                                                >
                                                    📄 View Uploaded Document
                                                </a>
                                            </div>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="modal-actions-prospect-manage">
                                        <button
                                            className="btn-primary-prospect-manage"
                                            onClick={handleUpdate}
                                            disabled={isEditFormInvalid}
                                        >
                                            Save
                                        </button>

                                        <button
                                            className="btn-secondary-prospect-manage"
                                            onClick={() => setEditData(null)}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {deleteTarget && (
                            <div className="edit-modal-overlay">
                                <div className="edit-modal" style={{ width: 400 }}>
                                    <div className="modal-header">
                                        <h3>Confirm Delete</h3>
                                    </div>

                                    <div className="modal-body">
                                        <p>
                                            Are you sure you want to <strong>permanently delete</strong> this prospect?
                                        </p>
                                        <p style={{ color: 'red', fontWeight: 600 }}>
                                            {deleteTarget.prospectName}
                                        </p>
                                    </div>

                                    <div className="modal-footer">
                                        <button
                                            className="btn-primary-prospect-manage"
                                            onClick={async () => {
                                                await api.delete(
                                                    `${API_BASE_URL}/api/manage-prospects/permanent/${deleteTarget._id}`
                                                );
                                                setDeleteTarget(null);
                                                loadProspects();
                                            }}
                                        >
                                            Yes, Delete
                                        </button>

                                        <button
                                            className="btn-secondary-prospect-manage"
                                            onClick={() => setDeleteTarget(null)}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {bulkLostModal.open && (
                            <div className="edit-modal-overlay" onClick={() => setBulkLostModal({ open: false, reason: '' })}>
                                <div className="edit-modal" onClick={(e) => e.stopPropagation()} style={{ width: 520 }}>
                                    <h3>Mark Selected Prospects as Lost</h3>
                                    <div className="modal-field">
                                        <label>Reason for Lost *</label>
                                        <textarea
                                            value={bulkLostModal.reason}
                                            onChange={(e) => setBulkLostModal((prev) => ({ ...prev, reason: e.target.value }))}
                                            placeholder="Enter reason for all selected prospects..."
                                            required
                                        />
                                    </div>
                                    <div className="modal-actions-prospect-manage">
                                        <button
                                            className="btn-danger-prospect-manage"
                                            onClick={async () => {
                                                const reason = String(bulkLostModal.reason || '').trim();
                                                if (!reason) {
                                                    alert('Reason is required');
                                                    return;
                                                }

                                                const ids = [...new Set(selectedProspectIds)];
                                                if (!ids.length) {
                                                    alert('No prospects selected');
                                                    return;
                                                }

                                                for (const id of ids) {
                                                    await api.put(`${API_BASE_URL}/api/manage-prospects/disable/${id}`, { reason });
                                                }

                                                setBulkLostModal({ open: false, reason: '' });
                                                setSelectedProspectIds([]);
                                                loadProspects();
                                            }}
                                        >
                                            Confirm Lost ({selectedProspectIds.length})
                                        </button>
                                        <button
                                            className="btn-secondary-prospect-manage"
                                            onClick={() => setBulkLostModal({ open: false, reason: '' })}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                    </section>
                </div>
            </div>
        </div>
    );
};

export default RetailProspectManage;
